import type * as React from 'react';
import { graphql } from 'react-apollo';

import { Specialization_ProductVariant } from '__generated__/graphql-types';
import { compose, withProps } from 'recompose';

import S12nDataQuery from 'bundles/career-plans/components/data/S12nDataQuery.graphql';
import type { S12nData_SpecializationFragment } from 'bundles/career-plans/components/data/__generated__/S12nDataQuery';
import getAllMastertracks from 'bundles/career-plans/components/data/getAllMastertracks.graphql';
import { CourseDataQuery, DegreesDataQuery2, DegreesDataQuery4 } from 'bundles/career-plans/components/data/queries';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';
import {
  ExternalCertificateS12n,
  GoogleCertificateS12n,
  NormalS12n,
  ProfessionalCertificateS12n,
} from 'bundles/s12n-common/constants/s12nProductVariants';

type ProductContentfulData = {
  id: string;
  isCourse: boolean;
  isSpecialization: boolean;
  isProject: boolean;
  isProfessionalCertificate: boolean;
  isMasterTrack: boolean;
};
type ProductContent = {
  id?: string;
  name?: string;
  s12nProductVariant?: S12nProductVariant;
  courseCount?: number;
  description?: string;
  averageFiveStarRating?: number;
  ratingCount?: number;
  imageUrl?: string;
  partners?: { id: string; name: string; squareLogo?: string }[];
  type?: string;
  objectUrl?: string;
  loaded?: boolean;
};
type CourseWithData = ProductContentfulData & ProductContent;
type CareerWithCourseData = {
  foundationalCourses: CourseWithData[];
  showcasedCourses: CourseWithData[];
};
type PageContent = {
  careers: CareerWithCourseData[];
};
type FormattedResult = {
  pageContent: PageContent;
  degrees: $TSFixMe;
};

const filterForCourses = (productInfo: $TSFixMe) => productInfo.isCourse || productInfo.isProject;
const filterForS12ns = (productInfo: $TSFixMe) => productInfo.isSpecialization || productInfo.isProfessionalCertificate;
const filterForMasterTracks = (productInfo: $TSFixMe) => productInfo.isMasterTrack;
const pullIdFromProductInfo = (productInfo: $TSFixMe) => productInfo.id;

const formatCourse = ({
  id,
  slug,
  name,
  description,
  photoUrl,
  partners,
  courseDerivativesV2,
}: $TSFixMe): ProductContent => {
  const { averageFiveStarRating, ratingCount } = courseDerivativesV2;
  const { elements: partnersElements } = partners;
  const computedLogo = (partnersElements && partnersElements[0]?.squareLogo) || photoUrl;

  return {
    id,
    name,
    description,
    averageFiveStarRating,
    ratingCount,
    imageUrl: computedLogo,
    partners: partnersElements,
    type: 'Course',
    objectUrl: `/learn/${slug}`,
    loaded: true,
  };
};
/** Temporary conversion for compatibility while we update this code to federated GraphQL. */
const convertS12nProductVariant = (variant: Specialization_ProductVariant): S12nProductVariant | undefined => {
  switch (variant) {
    case Specialization_ProductVariant.ExternalCertificateS12N:
      return ExternalCertificateS12n;
    case Specialization_ProductVariant.GoogleCertificateS12N:
      return GoogleCertificateS12n;
    case Specialization_ProductVariant.NormalS12N:
      return NormalS12n;
    case Specialization_ProductVariant.ProfessionalCertificateS12N:
      return ProfessionalCertificateS12n;
    default:
      return undefined;
  }
};
const formatSpecialization = (s12n: S12nData_SpecializationFragment): ProductContent => {
  const partners = s12n.partners;
  const computedLogo = (partners && partners[0]?.squareLogo) || s12n.cardImageUrl;

  return {
    id: s12n.id,
    name: s12n.name,
    s12nProductVariant: convertS12nProductVariant(s12n.productVariant),
    courseCount: s12n.courses?.length,
    description: s12n.descriptionAsPlainText,
    averageFiveStarRating: s12n.rating?.averageFiveStarRating,
    ratingCount: s12n.rating?.ratingCount,
    imageUrl: computedLogo,
    // @ts-expect-error URL: string
    partners,
    type: 'Specialization',
    objectUrl: `/specializations/${s12n.slug}`,
    loaded: true,
  };
};

const formatMasterTrack = ({ id, slug, name, thumbnailUrl, partners }: $TSFixMe): ProductContent => {
  const computedLogo = (partners && partners[0]?.logo) || thumbnailUrl;

  return {
    id,
    name,
    partners,
    imageUrl: computedLogo,
    type: 'MasterTrack',
    objectUrl: `/mastertrack/${slug}`,
    loaded: true,
  };
};

const rebuildWithProductData = ({
  products,
  courses = [],
  s12ns = [],
  mastertracks = [],
}: {
  products: ProductContentfulData[];
  courses: ProductContent[];
  s12ns: ProductContent[];
  mastertracks: ProductContent[];
}): CourseWithData[] => {
  if (!products) return [];

  return products.map((productContentfulData: ProductContentfulData): CourseWithData => {
    const { isCourse, isSpecialization, isProject, isProfessionalCertificate, isMasterTrack, id } =
      productContentfulData;

    const lookupProductById = ({ id: idFromResolvedProductData }: ProductContent) => id === idFromResolvedProductData;
    /* Due to inconsitencies between the returned IDs on mastertracks VS other products, a direct match between
     *  the supplied and returned values is not possible. In this situation, returned mastertrack IDs include
     *  additional key values in the string before the associated ID.
     *
     *  A key supplied on contentful might look like this: '7JkWHhPNTKCl-9vLoyPfFA'
     *  Whereas a key returned via the API takes a form similar to this: 'credentialTrack~d1w5DTCeR5WOT5l6iRUYuA'
     */
    const inferAssociationById = ({ id: idFromResolvedProductData }: ProductContent) =>
      idFromResolvedProductData && idFromResolvedProductData.includes(id);

    let productContent: ProductContent | undefined;
    if (isCourse) productContent = courses.find(lookupProductById);
    if (isProject) productContent = courses.find(lookupProductById);
    if (isSpecialization) productContent = s12ns.find(lookupProductById);
    if (isProfessionalCertificate) productContent = s12ns.find(lookupProductById);
    if (isMasterTrack) productContent = mastertracks.find(inferAssociationById);

    const resolvedProductContent: ProductContent = productContent || {};

    return {
      ...productContentfulData,
      ...resolvedProductContent,
    };
  });
};

const CourseQueryOptions = {
  alias: 'withCourseData',
  options: ({ courseIds }: $TSFixMe) => ({ variables: { courseIds } }),
  skip: ({ courseIds }: $TSFixMe) => !courseIds || (courseIds && !courseIds.length),
  props: ({ data: { CoursesV1Resource, loading } }: $TSFixMe) => {
    return {
      courses: CoursesV1Resource?.multiGet?.elements || [],
      loading,
    };
  },
};

const S12nQueryOptions = {
  alias: 'withS12nData',
  options: ({ s12nIds }: $TSFixMe) => ({
    variables: { s12nIds },
    context: { clientName: 'gatewayGql' },
  }),
  skip: ({ s12nIds }: $TSFixMe) => !s12nIds || (s12nIds && !s12nIds.length),
  props: ({ data: { Specialization, loading } }: $TSFixMe) => {
    return {
      s12ns: Specialization?.queryByIds || [],
      loading,
    };
  },
};

const GetAllMastertracksOptions = {
  alias: 'withMasterTracksData',
  options: {
    context: {
      clientName: 'gatewayGql',
    },
  },
  skip: ({ mastertrackIds }: $TSFixMe) => !mastertrackIds || (mastertrackIds && !mastertrackIds.length),
  props: ({
    data,
    loading,
  }: {
    loading: boolean;
    data?: { PremiumProductCollections?: { queryCollection: Array<{}> } };
  }) => ({
    mastertracks: data?.PremiumProductCollections?.queryCollection || [],
    loading,
  }),
};

const DegreesQuery4Options = {
  alias: 'withDegreeData',
  options: ({ pageContent }: $TSFixMe) => {
    const [degreeSlug1, degreeSlug2, degreeSlug3, degreeSlug4] = pageContent?.degrees || [];

    return {
      variables: { degreeSlug1, degreeSlug2, degreeSlug3, degreeSlug4 },
    };
  },
  skip: ({ pageContent }: $TSFixMe) => (pageContent?.degrees || []).length !== 4,
  props: ({ data: { DegreesV1Resource, loading } }: $TSFixMe) => {
    const degree1 = DegreesV1Resource?.degrees1?.elements || [];
    const degree2 = DegreesV1Resource?.degrees2?.elements || [];
    const degree3 = DegreesV1Resource?.degrees3?.elements || [];
    const degree4 = DegreesV1Resource?.degrees4?.elements || [];

    const compiledDegrees = [...degree1, ...degree2, ...degree3, ...degree4];

    return {
      degrees: compiledDegrees,
      loading,
    };
  },
};

const DegreesQuery2Options = {
  alias: 'withDegreeData',
  options: ({ pageContent }: $TSFixMe) => {
    const [degreeSlug1, degreeSlug2] = pageContent?.degrees || [];

    return {
      variables: { degreeSlug1, degreeSlug2 },
    };
  },
  skip: ({ pageContent }: $TSFixMe) => (pageContent?.degrees || []).length !== 2,
  props: ({ data: { DegreesV1Resource, loading } }: $TSFixMe) => {
    const degree1 = DegreesV1Resource?.degrees1?.elements || [];
    const degree2 = DegreesV1Resource?.degrees2?.elements || [];

    const compiledDegrees = [...degree1, ...degree2];

    return {
      degrees: compiledDegrees,
      loading,
    };
  },
};

/* Used in a reduce function in the first `withProps` statement. This pulls product IDs from
 *  an associated career's `foundationalCourses` and `showcasedCourses` properties, then compiles
 *  the corresponding IDs into other props for use in product lookup via `CourseDataQuery`,
 *  `S12nDataQuery`, and `DegreeDataQuery`, respectfully. The IDs for mastertracks are used later
 *  to filter out relevant mastertracks from a getAll query.
 */
const compileProductIds = (rObj: $TSFixMe, career: $TSFixMe) => {
  const { compiledCourseIds, compiledS12nIds, compiledMastertrackIds } = rObj;
  const { foundationalCourses = [], showcasedCourses = [] } = career;

  const foundationalCoursesFromCareer = foundationalCourses.items.filter(filterForCourses);
  const foundationalS12nsFromCareer = foundationalCourses.items.filter(filterForS12ns);
  const foundationalMasterTracksFromCareer = foundationalCourses.items.filter(filterForMasterTracks);
  const showcasedCoursesFromCareer = showcasedCourses.items.filter(filterForCourses);
  const showcasedS12nsFromCareer = showcasedCourses.items.filter(filterForS12ns);
  const showcasedMasterTracksFromCareer = showcasedCourses.items.filter(filterForMasterTracks);

  const courseIds = [...foundationalCoursesFromCareer, ...showcasedCoursesFromCareer].map(pullIdFromProductInfo);
  const s12nIds = [...foundationalS12nsFromCareer, ...showcasedS12nsFromCareer].map(pullIdFromProductInfo);
  const mastertrackIds = [...foundationalMasterTracksFromCareer, ...showcasedMasterTracksFromCareer].map(
    pullIdFromProductInfo
  );

  return {
    compiledCourseIds: [...compiledCourseIds, ...courseIds],
    compiledS12nIds: [...compiledS12nIds, ...s12nIds],
    compiledMastertrackIds: [...compiledMastertrackIds, ...mastertrackIds],
  };
};

export default <T extends object>(Component: React.ComponentType<T>): any =>
  compose<any, T>(
    withProps<{}, any>(({ pageContent }) => {
      const { careers, socialProofStatistics, testimonials, faqs } = pageContent || {};

      if (!careers) return {};

      // Pulling course and s12n IDs out of the careers object so that we can use them for
      // look up.
      const {
        compiledCourseIds: courseIds,
        compiledS12nIds: s12nIds,
        compiledMastertrackIds: mastertrackIds,
      } = careers?.items?.reduce(compileProductIds, {
        compiledCourseIds: [],
        compiledS12nIds: [],
        compiledMastertrackIds: [],
      });

      // Adjusting socialProofStatistics to follow the correct format
      const formattedSocialProofStatistics = socialProofStatistics?.items?.map(({ title, subtitle }: $TSFixMe) => ({
        description: title,
        value: subtitle,
      }));

      // Adjusting testimonials to follow the correct format
      const formattedTestimonials = testimonials?.items?.map(
        ({ title, subtitle, description, callToActionLink, visual }: $TSFixMe) => ({
          name: title,
          job: subtitle,
          text: description,
          readMoreUrl: callToActionLink,
          imageSrc: visual.url,
          remoteImg: true,
        })
      );

      return {
        socialProofStatistics: formattedSocialProofStatistics,
        testimonials: formattedTestimonials,
        courseIds,
        s12nIds,
        mastertrackIds,
        faq: faqs.items,
      };
    }),
    graphql(CourseDataQuery, CourseQueryOptions),
    // TODO: Typing
    graphql(S12nDataQuery, S12nQueryOptions),
    // TODO: Typing
    graphql(DegreesDataQuery4, DegreesQuery4Options),
    // TODO: Typing
    graphql(DegreesDataQuery2, DegreesQuery2Options),
    // @ts-expect-error TODO: Typing
    graphql(getAllMastertracks, GetAllMastertracksOptions),
    withProps(
      ({
        courses,
        s12ns,
        degrees: degreesFromLookup,
        mastertracks,
        pageContent,
      }: {
        courses: $TSFixMe;
        s12ns: S12nData_SpecializationFragment[];
        degrees: $TSFixMe;
        mastertracks: $TSFixMe;
        pageContent: $TSFixMe;
      }): FormattedResult => {
        const { careers } = pageContent;

        const formattedCourses = courses && courses.map(formatCourse);
        const formattedS12ns = s12ns && s12ns.map(formatSpecialization);
        const formattedMasterTracks = mastertracks && mastertracks.map(formatMasterTrack);

        const careersWithCourseData: CareerWithCourseData[] = careers?.items?.map(
          (careerData: $TSFixMe): CareerWithCourseData => {
            const { foundationalCourses, showcasedCourses } = careerData;

            const foundationalCoursesWithData = rebuildWithProductData({
              products: foundationalCourses.items,
              courses: formattedCourses,
              s12ns: formattedS12ns,
              mastertracks: formattedMasterTracks,
            });
            const showcasedCoursesWithData = rebuildWithProductData({
              products: showcasedCourses.items,
              courses: formattedCourses,
              s12ns: formattedS12ns,
              mastertracks: formattedMasterTracks,
            });

            return {
              ...careerData,
              foundationalCourses: foundationalCoursesWithData,
              showcasedCourses: showcasedCoursesWithData,
            };
          }
        );

        const formattedDegrees =
          degreesFromLookup &&
          degreesFromLookup.map(
            ({ id, name: degreeName, slug, logo, partners: { elements: partnerElements } }: $TSFixMe) => {
              const partnerLogos = partnerElements.map(({ squareLogo }: $TSFixMe) => squareLogo);
              const partnerNames = partnerElements.map(({ name }: $TSFixMe) => name);

              return {
                slug,
                partnerLogos,
                name: degreeName,
                imageUrl: logo,
                partners: partnerNames,
                objectID: id,
                objectUrl: `/degrees/${slug}`,
              };
            }
          );

        const pageContentWithUpdatedCareers: PageContent = {
          ...pageContent,
          careers: careersWithCourseData,
        };

        return {
          pageContent: pageContentWithUpdatedCareers,
          degrees: formattedDegrees,
        };
      }
    )
  )(Component);
